// Mock data for Tanmay Agarwal's portfolio
// Tight outer copy + full long-form case study content available via card click.

export const profile = {
  name: "Tanmay Agarwal",
  shortName: "Tanmay",
  tagline: "Builder \u2192 Backer.",
  location: "London, UK",
  email: "tansym123@gmail.com",
  phone: "+91-8107237564",
  linkedin: "https://www.linkedin.com/in/tanmay-a-aa4338108/",
  headshot: "/assets/tanmay.jpg",
  cv: "/assets/Tanmay_Agarwal_CV_2027_Final.pdf",
  currentRole: "Imperial MBA \u201927",
  statusChip: "AVAILABLE \u00b7 LONDON \u00b7 OPEN TO PRODUCT + VC ROLES \u00b7 AUG \u201927",
  oneLiner:
    "Six years inside seventeen startups. Co-built a $17M Series B. Shipped AI products to 150+ enterprises. Wrote my first angel cheque. Imperial MBA next \u2014 open to senior product roles and early-stage VC seats in London from August 2027."
};

// 3-step thesis: builder → backer → bridge
export const thesis = {
  eyebrow: "\u00a7 The arc",
  heading: "Built. Backed. Bridging.",
  steps: [
    {
      id: "operator",
      kicker: "01",
      tag: "BUILT",
      title: "Inside the building",
      bullets: [
        "Pixis (Chiratae Ventures)",
        "xto10x (Binny Bansal)",
        "Ather Energy \u00b7 pre-IPO",
        "PeopleCues \u00b7 150+ enterprises"
      ],
      color: "#FF8A5B"
    },
    {
      id: "angel",
      kicker: "02",
      tag: "BACKED",
      title: "First cheque written",
      bullets: [
        "Angel \u2192 ONYA",
        "Conviction over consensus",
        "Founder-market fit > trend",
        "Ran 25+ investor convos at Pixis"
      ],
      color: "#FFD2A6"
    },
    {
      id: "vc",
      kicker: "03",
      tag: "NEXT",
      title: "Picking what's next",
      bullets: [
        "Imperial MBA \u201927",
        "Open to senior product roles",
        "Open to early-stage VC seats",
        "London, full-time from Aug \u201927"
      ],
      color: "#FFF3E9"
    }
  ]
};

// Brutalist 4-stat block
export const bigNumbers = [
  { stat: "$17M", label: "Series B raised", note: "Pixis \u00b7 led by Chiratae" },
  { stat: "1M+", label: "Data points modeled", note: "PeopleCues" },
  { stat: "150+", label: "Enterprise deployments", note: "India \u00b7 MENA \u00b7 SG" },
  { stat: "5\u00d7", label: "Pipeline growth", note: "International GTM" }
];

// Career receipts \u2014 each card now carries full long-form content for the modal.
export const receipts = [
  {
    id: "imperial",
    year: "2025\u201327",
    company: "Imperial College London",
    role: "Full-time MBA",
    badge: "NOW",
    color: "#FF8A5B",
    line: "Picked Imperial for one reason: the pivot into venture. FT top-25.",
    metric: "MBA \u201927",
    fullTitle: "Imperial College London \u2014 The Bridge",
    tagline: "Operator lens, sharpened by an MBA, brought to early-stage venture in London",
    summary:
      "Full-time MBA at Imperial College Business School (Class of 2027). Top-25 globally per the Financial Times. The bridge from six years operating to early-stage investing in London.",
    companyNote:
      "Imperial College London is one of London's leading business schools and consistently ranked in the global top 25 by the FT.",
    sections: [
      {
        heading: "Why Imperial",
        body:
          "Picked Imperial for one reason: the pivot into venture. London is the right ecosystem; Imperial is the right network. VC-track electives, the Imperial Innovation ecosystem, and proximity to LP and GP networks across Mayfair and the City."
      },
      {
        heading: "What I'm using it for",
        body:
          "Sharpening the investor lens \u2014 finance, valuation, fund construction. Building relationships across European VCs and operators. Re-baselining the operator playbook against US, EU and Asian benchmarks I haven't seen up close."
      },
      {
        heading: "What I'm targeting next",
        body:
          "Summer internship at a London early-stage VC during the MBA, full-time from August 2027. Sector-agnostic, stage-curious. Open to all stages but the operator instinct biases me toward early."
      }
    ]
  },
  {
    id: "onya",
    year: "2025",
    company: "ONYA",
    role: "Angel investor",
    badge: "1ST CHEQUE",
    color: "#FFD2A6",
    line: "First cheque written. Founder-led. Conviction over consensus.",
    metric: "1st cheque",
    fullTitle: "Investing \u2014 ONYA",
    tagline: "First early-stage cheque; building an operator-led investing thesis",
    summary:
      "Writing early-stage angel cheques into operator-led startups. First investment: ONYA. Thesis grounded in founder-market fit, clarity of problem, and execution signal \u2014 not trend-chasing.",
    companyNote:
      "ONYA is an early-stage consumer startup; first cheque in a portfolio being built around operator-led theses.",
    sections: [
      {
        heading: "Approach",
        body:
          "Evaluating opportunities on founder-market fit, clarity of the problem being solved, and early indicators of execution strength. The operator lens \u2014 built from 6 years inside 17 startups \u2014 helps me stress-test whether a product can realistically scale and whether the team can execute under uncertainty."
      },
      {
        heading: "Investment",
        body:
          "Early-stage cheque into ONYA \u2014 the first of a longer-term shift toward backing the problems and teams I'd want to build alongside."
      },
      {
        heading: "Key Learning",
        body:
          "Investing shifts your thinking from solving problems to selecting them."
      }
    ]
  },
  {
    id: "peoplecues",
    year: "2024\u201325",
    company: "xto10x \u00b7 PeopleCues",
    role: "Product Owner",
    badge: "AI / SAAS",
    color: "#FFF3E9",
    line: "Shipped CLO \u2014 the AI nudging engine that lifted feedback quality 40\u201360%.",
    metric: "1M+ data points",
    fullTitle: "PeopleCues \u2014 AI Feedback Intelligence",
    tagline: "Turning vague employee feedback into decision intelligence",
    summary:
      "Built CLO, an AI nudging engine that improved feedback depth by 40\u201360% and cut reviewer effort by 60%+ \u2014 repositioning PeopleCues from a survey tool to a decision intelligence system.",
    companyNote:
      "xto10x is a Bangalore-based venture studio founded by Binny Bansal (co-founder of Flipkart). PeopleCues is its enterprise performance & engagement SaaS.",
    sections: [
      {
        heading: "Context",
        body:
          "PeopleCues is xto10x's enterprise engagement platform, deployed across companies like Meesho (India's largest horizontal e-commerce marketplace), CRED (leading Indian fintech unicorn) and Ather Energy (premier Indian EV maker). Adoption was strong and data volumes were huge \u2014 but actual utility for decision-making was limited."
      },
      {
        heading: "Problem",
        body:
          "The issue wasn't data availability \u2014 it was data quality. Employees defaulted to vague answers: 'culture is good', 'management can improve'. Leaders had a flood of feedback and a very low signal-to-noise ratio. Weak decisions. Poor ROI on engagement."
      },
      {
        heading: "Insight",
        body:
          "After analysing 1M+ data points across 150+ organisations, one behavioural pattern stood out: users default to vague feedback when there is cognitive, emotional or structural friction. The right question wasn't 'how do we collect feedback?' \u2014 it was 'how do we make users give better feedback?'"
      },
      {
        heading: "Solution \u2014 CLO (Chief Listening Officer)",
        body:
          "An AI nudging layer that analyses responses in real time, detects vague answers, and triggers contextual prompts \u2014 'can you share a specific example?', 'what exactly could be improved?' \u2014 without breaking the flow of participation."
      },
      {
        heading: "Product Design Challenges",
        body:
          "Three tensions to resolve: when to interrupt (too early annoys, too late is ineffective); tone (helpful, not critical); and the behavioural balance \u2014 improving quality without reducing participation. Solved through iterative prompt design, trigger logic, and UI placement with design and engineering."
      },
      {
        heading: "Outcome",
        body:
          "40\u201360% lift in feedback depth and clarity, 60%+ reduction in reviewer effort. CLO became a core differentiator in enterprise sales \u2014 directly impacting retention and new enterprise acquisition. PeopleCues now positioned as a decision intelligence platform."
      },
      {
        heading: "Key Learning",
        body:
          "Product is not just about building tools \u2014 it is about shaping user behaviour."
      }
    ]
  },
  {
    id: "global",
    year: "2024\u201325",
    company: "PeopleCues \u00b7 Global",
    role: "Product + GTM",
    badge: "INTL",
    color: "#FF8A5B",
    line: "Took the platform to Singapore, KSA, Qatar, UAE \u2014 5\u00d7 pipeline.",
    metric: "5\u00d7 pipeline",
    fullTitle: "Global Expansion \u2014 PeopleCues",
    tagline: "Scaling product & GTM across Singapore and the Middle East",
    summary:
      "Led international deployment of PeopleCues across Singapore, Saudi Arabia, Qatar and the UAE \u2014 generating 5\u00d7 enterprise pipeline growth and validating the platform's global scalability.",
    companyNote:
      "PeopleCues was extended beyond India into Singapore, Saudi Arabia, Qatar and the UAE.",
    sections: [
      {
        heading: "Context",
        body:
          "PeopleCues had strong product-market fit in India. The next test was whether it could travel: Singapore and the Middle East had materially different organisational maturity and cultural expectations from feedback systems."
      },
      {
        heading: "Approach",
        body:
          "Adapted workflows to match local organisational maturity and cultural nuance. Refined positioning and GTM per region, and worked closely with enterprise clients to tailor each implementation without fragmenting the core product."
      },
      {
        heading: "Outcome",
        body:
          "Successful rollouts across Saudi Arabia, Qatar, Dubai and Singapore. 5\u00d7 enterprise pipeline growth and validated scalability of the product internationally."
      },
      {
        heading: "Key Learning",
        body:
          "A product doesn't scale globally unless its assumptions do."
      }
    ]
  },
  {
    id: "ather",
    year: "2023",
    company: "Ather Energy",
    role: "Consulting lead",
    badge: "PRE-IPO",
    color: "#FFD2A6",
    line: "Designed the OKR machine pre-IPO. Listed NSE 2025.",
    metric: "L1\u2192L4 cascade",
    fullTitle: "Ather Energy \u2014 Execution Systems at Scale",
    tagline: "OKR operating system during a pre-IPO growth phase",
    summary:
      "Designed an OKR operating system cascading goals from L1 to L4 across Ather Energy during its pre-IPO phase \u2014 improving alignment and execution discipline ahead of a 2025 public listing.",
    companyNote:
      "Ather Energy is India's premier electric two-wheeler manufacturer, listed on the NSE in 2025 with a valuation of ~$1.4B.",
    sections: [
      {
        heading: "Context",
        body:
          "Engaged with Ather Energy \u2014 India's premier EV two-wheeler company \u2014 during a critical pre-IPO, high-growth phase. Strong leadership, strong product, but priorities were fragmenting across a fast-growing organisation."
      },
      {
        heading: "Insight",
        body:
          "Startups at scale rarely fail for lack of strategy. They fail for lack of alignment. Strategy without rhythm is noise."
      },
      {
        heading: "Solution",
        body:
          "Designed an OKR-driven operating system: goal cascade from L1 (founders) to L4 (ICs), structured execution cadences, and clarified ownership across teams. Paired with org-structure alignment to make accountability unambiguous."
      },
      {
        heading: "Challenges",
        body:
          "Navigating differing perspectives between founders and CXOs. Driving adoption across a large, fast-moving organisation where every week was already oversubscribed."
      },
      {
        heading: "Outcome",
        body:
          "Improved alignment, stronger execution discipline, and clearer accountability. Contributed to Ather's ability to scale effectively through the high-growth window that preceded its 2025 IPO."
      },
      {
        heading: "Key Learning",
        body:
          "Execution systems are the backbone of scaling companies."
      }
    ]
  },
  {
    id: "10xgoals",
    year: "2022\u201324",
    company: "xto10x \u00b7 10xGoals",
    role: "PM + Consultant",
    badge: "SAAS",
    color: "#FFF3E9",
    line: "Turned consulting hours into a SaaS engine. 15+ scaleups onboarded.",
    metric: "15+ startups",
    fullTitle: "10xGoals \u2192 PeopleCues",
    tagline: "Productising a consulting framework into scalable SaaS",
    summary:
      "Led the transition from 10xGoals \u2014 a high-touch OKR consulting practice used by 15+ startups \u2014 into PeopleCues, a repeatable SaaS deployable across many organisations simultaneously.",
    companyNote:
      "10xGoals was xto10x's OKR and execution consulting practice, used by 15+ high-growth Indian startups.",
    sections: [
      {
        heading: "Context",
        body:
          "xto10x ran deep engagements helping Indian startups implement OKRs, execution systems and alignment frameworks. The work was impactful but inherently unscalable \u2014 dependent on expert intervention and per-client customisation."
      },
      {
        heading: "Insight",
        body:
          "The real asset was never the consulting hours. It was the structured frameworks, repeatable execution systems and founder playbooks underneath \u2014 and those could be codified into software."
      },
      {
        heading: "Solution",
        body:
          "Led the transition from framework to product: defined the system for goal-setting, tracking and alignment; scoped an MVP that prioritised core workflows over feature overload; translated abstract strategy into clear interfaces and actionable dashboards."
      },
      {
        heading: "Challenges",
        body:
          "Holding the tension between consulting flexibility and product standardisation. Relentlessly cutting edge-case complexity that belongs in services, not in a product."
      },
      {
        heading: "Outcome",
        body:
          "Scalable SaaS deployable across multiple organisations in parallel \u2014 the long-term product foundation PeopleCues is built on today, now serving 150+ enterprises."
      },
      {
        heading: "Key Learning",
        body:
          "The highest-leverage move is converting knowledge into systems."
      }
    ]
  },
  {
    id: "pixis-fundraise",
    year: "2021\u201322",
    company: "Pixis",
    role: "Supporting CEO",
    badge: "FUNDRAISE",
    color: "#FF8A5B",
    line: "Co-built the model + narrative behind a $17M Series B.",
    metric: "$17M raise",
    fullTitle: "Pixis \u2014 Series B Fundraising",
    tagline: "Building the investable narrative behind a $17M raise",
    summary:
      "Worked alongside the CEO across 25+ investor conversations \u2014 building financial models, stress-testing growth assumptions, and shaping the narrative behind a $17M Series B led by Chiratae Ventures.",
    companyNote:
      "Chiratae Ventures is a leading Indian early-stage VC firm (formerly IDG Ventures India), an early backer of Lenskart, Myntra and Uniphore.",
    sections: [
      {
        heading: "Context",
        body:
          "Pixis was raising a $17M Series B. A complex AI product needed to be made simple, defensible and compelling for a wide range of global investors."
      },
      {
        heading: "Role",
        body:
          "Built financial models and scenario analyses. Shaped market positioning and the product story. Ran investor feedback loops across 25+ conversations, iterating the pitch based on recurring patterns."
      },
      {
        heading: "Outcome",
        body:
          "Successful $17M Series B raise led by Chiratae Ventures. Sharper internal alignment between product, business and capital expectations that outlasted the round itself."
      },
      {
        heading: "Key Learning",
        body:
          "Fundraising is about clarity, not just performance."
      }
    ]
  },
  {
    id: "pixis-insights",
    year: "2020\u201322",
    company: "Pixis Insights",
    role: "Entrepreneur in Residence",
    badge: "AI / RESEARCH",
    color: "#FFD2A6",
    line: "Built a real-time research stack \u2014 ~60% cheaper than incumbents.",
    metric: "~60% cost cut",
    fullTitle: "Pixis Insights",
    tagline: "Real-time market research infrastructure",
    summary:
      "Contributed to building Pixis Insights \u2014 integrating panel providers Lucid & Cint to automate respondent sourcing, cutting enterprise research cost by ~60% while preserving statistical rigor.",
    companyNote:
      "Pixis is a Chiratae Ventures-backed AI marketing infrastructure company, building codeless AI for global brands.",
    sections: [
      {
        heading: "Context",
        body:
          "Pixis works with global brands like Amazon, OnePlus, Canon and HDFC Bank on AI-driven marketing. A major bottleneck was traditional market research \u2014 slow, expensive, and infrequent \u2014 leaving teams deciding on outdated insights."
      },
      {
        heading: "Insight",
        body:
          "If research could be automated, integrated and real-time, it could become a continuous decision-making engine \u2014 not a quarterly deliverable."
      },
      {
        heading: "Solution",
        body:
          "Contributed to building the survey infrastructure and led integrations with panel providers Lucid and Cint for automated respondent sourcing and real-time data flow. Redesigned workflows to strip cost and latency out of the pipeline."
      },
      {
        heading: "Challenges",
        body:
          "Maintaining data quality and statistical reliability while pushing cost down. Coordinating across external vendors and internal engineering with strict SLAs."
      },
      {
        heading: "Outcome",
        body:
          "Adopted by 12+ enterprise clients in year one with 90% repeat usage, including Amazon, OnePlus, Canon, Equitable and HDFC Bank. Enterprise research costs dropped by ~60%."
      },
      {
        heading: "Key Learning",
        body:
          "Great products improve both speed and economics \u2014 not just one."
      }
    ]
  },
  {
    id: "iimb",
    year: "2024",
    company: "IIM Bangalore \u00b7 NSRCEL",
    role: "Invited speaker",
    badge: "TALK",
    color: "#FFF3E9",
    line: "Took the IIMB stage. 50+ founders, one playbook.",
    metric: "50+ founders",
    fullTitle: "IIM Bangalore \u2014 One-off Founder Session",
    tagline: "NSRCEL Velocity \u00b7 Kotak BizLabs Bootcamp",
    summary:
      "Invited to deliver a one-off session for 50+ alumni founders at IIM Bangalore's NSRCEL incubator on OKRs in fast-scaling startups \u2014 the idea that 3\u20134 sharp founder priorities drive ~80% of outcomes.",
    companyNote:
      "IIM Bangalore is one of India's top-ranked business schools (often compared to LBS or INSEAD in Asia). NSRCEL is its startup incubator.",
    sections: [
      {
        heading: "Context",
        body:
          "A one-time invited session at NSRCEL's Velocity \u2014 Kotak BizLabs Bootcamp at IIM Bangalore (one of India's top business schools, comparable to LBS or INSEAD in Asia) for 50+ alumni founders scaling their startups. Not a recurring engagement \u2014 just one well-prepared session."
      },
      {
        heading: "Core Idea",
        body:
          "3\u20134 sharply defined founder priorities drive the majority of business outcomes. Aligning the whole team around those priorities creates compounding impact. The session was practical \u2014 systems and decisions, not theory \u2014 drawing from real engagements with Ather Energy and 15+ portfolio startups."
      },
      {
        heading: "Outcome",
        body:
          "Delivered to 50+ founders. The takeaway for me: articulating the operating system clearly \u2014 not just running it \u2014 is what compounds the lens I'll bring to investing."
      },
      {
        heading: "Key Learning",
        body:
          "Articulating a system clearly is the ultimate test of whether you actually understand it."
      }
    ]
  }
];

export const navLinks = [
  { label: "Arc", href: "#thesis" },
  { label: "Numbers", href: "#numbers" },
  { label: "Receipts", href: "#receipts" },
  { label: "Off-screen", href: "#off-screen" },
  { label: "Contact", href: "#contact" }
];

export const marqueeItems = [
  "xto10x \u00b7 Binny Bansal",
  "Pixis \u00b7 $17M Series B",
  "Ather Energy \u00b7 pre-IPO",
  "Angel \u2192 ONYA",
  "Imperial MBA \u201927",
  "Builder \u2192 Backer",
  "PeopleCues \u00b7 150+ enterprises",
  "Meesho \u00b7 CRED \u00b7 Ather",
  "IIM Bangalore \u00b7 NSRCEL",
  "Trinity Grade 6 Piano",
  "25+ countries"
];

export const hobbies = {
  eyebrow: "\u00a7 Off-screen",
  heading: "What I do when I'm",
  headingItalic: "not picking startups.",
  subhead:
    "People who compound also tend to live in colour. Here's mine.",
  cards: [
    { id: "travel", icon: "Plane", title: "25+ countries", subtitle: "Travel obsessive" },
    { id: "piano", icon: "Music", title: "Grade 6 Piano", subtitle: "Bach \u00b7 Chopin" },
    { id: "ai-tools", icon: "Sparkles", title: "Claude + Figma Make", subtitle: "Solo-build, AI-native" },
    { id: "food", icon: "UtensilsCrossed", title: "Foodie", subtitle: "Hunting the next great meal" },
    { id: "badminton", icon: "CircleDot", title: "Badminton", subtitle: "Smashes louder than my opinions" },
    { id: "pickleball", icon: "Volleyball", title: "Pickleball", subtitle: "Late convert" },
    { id: "snacks", icon: "Cookie", title: "Crunchy snacks", subtitle: "Homemade. Genuinely great." },
    { id: "running-gym", icon: "Dumbbell", title: "5 mornings a week", subtitle: "Running + gym" },
    { id: "netflix", icon: "Film", title: "Espionage dramas", subtitle: "Fauda \u00b7 The Spy" }
  ]
};
